import { FiltersType } from '@/api/types';
import { client } from '@/client';
import { MessageResponseModel } from '@/client/models';
import {
  CloneQuizMutation,
  CloneQuizRequest,
  DeleteQuizMutation,
  DeleteQuizRequest,
  ListQuizzesResponse,
  Quiz,
  QuizAnswerStatistics,
} from '@/client/quizzes';
import {
  useMutation,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { GenericForbiddenApiError } from './types';

export const useQuiz = (
  accountId: string,
  params: UseQueryOptions & {
    quizId?: string;
  } = {},
): UseQueryResult<Quiz> & {
  quiz: Quiz | undefined;
} => {
  const {
    quizId,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['quiz', accountId, quizId],
    () =>
      accountId && quizId
        ? client.quizzes.getQuiz(accountId, quizId)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as UseQueryResult<Quiz>),
    },
  );

  return {
    quiz: data,
    ...(rest as UseQueryResult<Quiz>),
  };
};

export const useSurveyForUser = (
  params: UseQueryOptions & {
    sentSurveyId?: string;
    token?: string;
  },
): UseQueryResult<Quiz> & {
  survey: Quiz | undefined;
} => {
  const {
    sentSurveyId,
    token,
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['surveyForUser', sentSurveyId, token],
    () =>
      sentSurveyId
        ? client.quizzes.getSurveyForUser(sentSurveyId, token)
        : undefined,
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as UseQueryResult<Quiz>),
    },
  );

  return {
    survey: data,
    ...(rest as UseQueryResult<Quiz>),
  };
};

export const useQuizzes = (
  accountId: string,
  params: UseQueryOptions & {
    filters?: FiltersType;
  },
): UseQueryResult<ListQuizzesResponse> & {
  quizzes: ListQuizzesResponse | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['quizzes', accountId, JSON.stringify(params.filters)],
    () => client.quizzes.getQuizzes(accountId, params),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as UseQueryResult<ListQuizzesResponse>),
    },
  );

  return {
    quizzes: data,
    ...(rest as UseQueryResult<ListQuizzesResponse>),
  };
};

export const useCloneQuiz = (): {
  clone: (payload: CloneQuizRequest) => Promise<Quiz>;
} & CloneQuizMutation => {
  const { mutateAsync, ...rest } = useMutation<
    Quiz,
    GenericForbiddenApiError,
    CloneQuizRequest
  >('cloneCampaign', (payload: CloneQuizRequest) =>
    client.quizzes.cloneQuiz(payload),
  );

  return { clone: mutateAsync, ...(rest as any) };
};

export const useDeleteQuiz = (): {
  delete: (payload: DeleteQuizRequest) => Promise<MessageResponseModel>;
} & DeleteQuizMutation => {
  const { mutateAsync, ...rest } = useMutation<
    MessageResponseModel,
    GenericForbiddenApiError,
    DeleteQuizRequest
  >('deleteSurvey', (payload: DeleteQuizRequest) =>
    client.quizzes.deleteQuiz(payload),
  );

  return { delete: mutateAsync, ...(rest as any) };
};

export const useQuizzesAnswersStatistics = (
  accountId: string,
  quizId: string,
  params: UseQueryOptions = {},
): UseQueryResult<QuizAnswerStatistics[]> & {
  quizzesAnswers: QuizAnswerStatistics[] | undefined;
} => {
  const {
    retry = false,
    retryDelay = 500,
    staleTime = 0,
    cacheTime = 0,
    ...restOptions
  } = params;

  const { data, ...rest } = useQuery(
    ['quizzesAnswers', accountId, quizId],
    () => client.quizzes.getQuizzesAnswersStatistics(accountId, quizId),
    {
      retry,
      retryDelay,
      staleTime,
      cacheTime,
      ...(restOptions as UseQueryResult<QuizAnswerStatistics[]>),
    },
  );

  return {
    quizzesAnswers: data,
    ...(rest as UseQueryResult<QuizAnswerStatistics[]>),
  };
};
